/**
 * Execution Events API — the 3-tier price-capture model:
 * events (tier 1) → fills (tier 2) → price captures (tier 3).
 *
 * entry/exit underlying are EFFECTIVE values (manual override beats the
 * fill-time capture); *_source says which one you're looking at.
 *
 * Read-only: the manual backfill and link writes were removed (2026-07-31) —
 * both were used twice, on migration day, and are superseded by the income
 * card's refetch path and the position-assign flow.
 */
import { apiGet } from './client'

export interface PriceCapture {
  id: number
  capture_type: 'UNDERLYING' | 'BASE'
  symbol: string
  price: number | null
  is_manual: boolean
  captured_at: string | null
  notes: string | null
}

export interface ExecFill {
  id: number
  exec_id: string
  order_id: string | null
  executed_at: string | null
  fill_type: 'OPEN' | 'CLOSE'
  quantity: number
  fill_price: number | null
  commission: number | null
  price_captures: PriceCapture[]
}

export interface ExecEvent {
  id: number
  broker_account_id: number | null
  broker_contract_id: string | null
  osi_symbol: string | null
  underlying: string | null
  symbol: string | null
  sec_type: string | null
  position_type: string | null
  strike: number | null
  expiry: string | null
  right: string | null
  total_open_qty: number
  total_close_qty: number
  net_qty: number
  avg_open_price: number | null
  avg_close_price: number | null
  entry_underlying: number | null
  exit_underlying: number | null
  entry_underlying_source: 'override' | 'cached'
  exit_underlying_source: 'override' | 'cached'
  status: string
  first_fill_at: string | null
  last_fill_at: string | null
  position_id: number | null
  tracker_trade_id: number | null
  is_base: boolean
  notes: string | null
  fills?: ExecFill[]
}

export interface ExecEventsResponse {
  count: number
  execution_events: ExecEvent[]
  limit: number
  offset: number
}

export const listExecEvents = (params: {
  underlying?: string
  status?: string
  unlinked?: boolean
  /** Server-side scope to one Tracker trade (trade_history_api supports it). */
  tracker_trade_id?: number
  limit?: number
  offset?: number
  account?: string
}) => apiGet<ExecEventsResponse>('/trade/execution-events', { params })

export const getExecEvent = (eventId: number) =>
  apiGet<ExecEvent>(`/trade/execution-events/${eventId}`)

